import React, { useState, useEffect } from 'react';
import { useDebouncedValue, useClickOutside, useWindowEvent } from '@mantine/hooks';
import {
  Modal,
  Box,
  Button,
  TextInput,
  Group,
  Text,
  Divider,
  Stack,
  Paper,
  ActionIcon,
} from '@mantine/core';

import {
  IconMinus,
  IconHash,
  IconPlaystationCircle,
  IconAlignLeft,
  IconAlignCenter,
  IconAlignRight,
  IconLetterCase,
  IconEyeOff,
  IconX,
} from "@tabler/icons";
import {useUserPermissions} from "../../../hooks/useUserPermissions";
import {
  IDEA_COLUMN_WIDTH_OPTIONS,
  getIdeaColumnSizeFromWidth,
  getIdeaColumnWidthNumber,
} from './columnWidthOptions';

const GAP_PX = 8;
const PANEL_WIDTH = 350;
const GAP_FROM_DRAWER_PX = 48;
const PANEL_MAX_VISIBLE_HEIGHT = 560;
const PANEL_VERTICAL_MARGIN = 26;

interface EditColumnModalProps {
  opened: boolean;
  onClose: () => void;
  column: any;
  anchorRect?: DOMRect;
  updateColumn: (column: any) => void;
}

export const EditColumnModal: React.FC<EditColumnModalProps> = ({ opened, onClose, column, anchorRect, updateColumn }) => {
  const [localColumn, setLocalColumn] = useState<any>(() => ({ ...column }));

  const [displayAs, setDisplayAs] = useState<string>(column?.renderComponent || 'TagsCell');
  const [width, setWidth] = useState<string>(() => getIdeaColumnSizeFromWidth(column?.width));
  const [alignment, setAlignment] = useState<string>(column?.textAlignment || 'left');
  const [title, setTitle] = useState<string>(column?.title || '');
  const [tooltip, setTooltip] = useState<string>(column?.tooltip || '');

  const [debouncedTitle] = useDebouncedValue(title, 400);
  const [debouncedTooltip] = useDebouncedValue(tooltip, 400);

  const {isAdmin} = useUserPermissions();

  const selectedBg = '#EEF0FA';
  const selectedBorder = '#5C5CEB';
  const unselectedBorder = '#D1D5DB';
  const selectedText = '#5C5CEB';
  const unselectedText = '#374151';

  // Sync working copy when modal opens or column prop changes (e.g. opening for a different column)
  useEffect(() => {
    if (opened && column) {
      setLocalColumn({ ...column });
    }
  }, [opened, column]);

  useEffect(() => {
    if (debouncedTitle !== (column?.title ?? '')) {
      updateField('title', debouncedTitle);
    }
  }, [debouncedTitle]);

  useEffect(() => {
    if (debouncedTooltip !== (column?.tooltip ?? '')) {
      updateField('tooltip', debouncedTooltip);
    }
  }, [debouncedTooltip]);

  const displayAsOptions = [
    { value: 'TextCell', label: 'Text', icon: IconLetterCase },
    { value: 'DynamicCell', label: 'Bar', icon: IconMinus },
    { value: 'TemplateCell', label: 'Num', icon: IconHash },
    { value: 'TagsCell', label: 'Badge', icon: IconPlaystationCircle },
  ];

  const widthOptions = IDEA_COLUMN_WIDTH_OPTIONS;

  const alignmentOptions = [
    { value: 'left', label: 'Left', icon: IconAlignLeft },
    { value: 'center', label: 'Center', icon: IconAlignCenter },
    { value: 'right', label: 'Right', icon: IconAlignRight },
  ];

  const updateColumnVisibility = () => {
    const updatedColumn = {
      ...localColumn,
      hidden: true
    };
    updateColumn(updatedColumn);
    onClose();
  };

  const getWidthNumber = (size: string) => getIdeaColumnWidthNumber(size);

  const updateField = (key: string, value: string | number) => {
    const updatedColumn = {
      ...localColumn,
      [key]: value
    };
    setLocalColumn(updatedColumn);
    updateColumn(updatedColumn);
  };

  const clickOutsideRef = useClickOutside<HTMLDivElement>(() => {
    if (opened && anchorRect) onClose();
  });
  useWindowEvent('keydown', (e) => {
    if (e.key === 'Escape' && opened && anchorRect) onClose();
  });

  const modalBodyContent = (
    <>
      <Divider mb="md" style={{ width: '100%' }} />
      <Stack spacing="md" ml={'15px'} mr={'15px'}>
        {/* Title Section */}
        <Box>
          <Text size="sm" weight={500} mb="xs">Title</Text>
          <TextInput
            value={title}
            disabled={ !isAdmin }
            onChange={(e) => setTitle(e.currentTarget.value)}
            placeholder="Enter title"
          />
        </Box>

        {/* Display As Section */}
        <Box>
          <Text size="sm" weight={500} mb="xs">Display As</Text>
          <Group spacing="xs">
            {displayAsOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = displayAs === option.value;
              return (
                <Button
                  key={option.value}
                  variant="default"
                  disabled={ !isAdmin }
                  onClick={() => {
                    setDisplayAs(option.value);
                    updateField('renderComponent', option.value);
                  }}
                  styles={{
                    root: {
                      flex: 1,
                      height: '60px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: isSelected ? `2px solid ${selectedBorder}` : `1px solid ${unselectedBorder}`,
                      borderRadius: '8px',
                      backgroundColor: isSelected ? selectedBg : '#ffffff',
                      padding: '8px 4px',
                      color: isSelected ? selectedText : unselectedText,
                      '&:hover': {
                        backgroundColor: isSelected ? selectedBg : '#E5E7EB',
                        borderColor: isSelected ? selectedBorder : unselectedBorder,
                      },
                    },
                    label: {
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      fontSize: '12px',
                      fontWeight: 400,
                      lineHeight: 1.2,
                      color: 'inherit',
                    },
                  }}
                >
                  <Icon 
                    size={24} 
                    style={{
                      color: isSelected ? selectedText : unselectedText,
                      strokeWidth: option.value === 'DynamicCell' ? 3 : 1.5
                    }} 
                  />
                  <Text size="xs" style={{ color: unselectedText }}>{option.label}</Text>
                </Button>
              );
            })}
          </Group>
        </Box>

        {/* Width Section */}
        <Box>
          <Text size="sm" weight={500} mb="xs">Width</Text>
          <Group spacing="xs">
            {widthOptions.map((option) => {
              const isSelected = width === option.value;
              return (
                <Button
                  key={option.value}
                  variant="default"
                  onClick={() => {
                    setWidth(option.value);
                    updateField('width', getWidthNumber(option.value));
                  }}
                  styles={{
                    root: {
                      flex: 1,
                      height: '36px',
                      border: isSelected ? `2px solid ${selectedBorder}` : `1px solid ${unselectedBorder}`,
                      borderRadius: '8px',
                      backgroundColor: isSelected ? selectedBg : '#ffffff',
                      padding: '0 12px',
                      color: isSelected ? selectedText : unselectedText,
                      '&:hover': {
                        backgroundColor: isSelected ? selectedBg : '#E5E7EB',
                        borderColor: isSelected ? selectedBorder : unselectedBorder,
                      },
                    },
                    label: {
                      fontSize: '14px',
                      fontWeight: isSelected ? 700 : 500,
                      color: 'inherit',
                    },
                  }}
                >
                  {option.label}
                </Button>
              );
            })}
          </Group>
        </Box>

        {/* Alignment Section */}
        <Box>
          <Text size="sm" weight={500} mb="xs">Alignment</Text>
          <Group spacing="xs">
            {alignmentOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = alignment === option.value;
              return (
                <Button
                  key={option.value}
                  variant="default"
                  onClick={() => {
                    setAlignment(option.value);
                    updateField('textAlignment', option.value);
                  }}
                  styles={{
                    root: {
                      flex: 1,
                      height: '36px',
                      border: isSelected ? `2px solid ${selectedBorder}` : `1px solid ${unselectedBorder}`,
                      borderRadius: '8px',
                      backgroundColor: isSelected ? selectedBg : '#ffffff',
                      padding: '0',
                      color: isSelected ? selectedText : unselectedText,
                      '&:hover': {
                        backgroundColor: isSelected ? selectedBg : '#E5E7EB',
                        borderColor: isSelected ? selectedBorder : unselectedBorder,
                      },
                    },
                    label: {
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'inherit',
                    },
                  }}
                >
                  <Icon 
                    size={18} 
                    style={{
                      color: isSelected ? selectedText : unselectedText,
                      strokeWidth: 2
                    }} 
                  />
                </Button>
              );
            })}
          </Group>
        </Box>

        {/* Tooltip Section */}
        <Box>
          <TextInput
            label={<Text size="sm" weight={500} mb="xs">Tooltip (optional)</Text>}
            placeholder="Shown on header hover"
            value={tooltip}
            disabled={ !isAdmin }
            onChange={(e) => setTooltip(e.currentTarget.value)}
          />
        </Box>
        <Button mt="xs" mb='sm' variant='default' onClick={() => updateColumnVisibility()}>
          <IconEyeOff
            size={16}
            color={'grey'}
          />
          <Text pl={'xs'} size="sm" style={{ color: '#495057' }}>Hide Column</Text>
        </Button>
      </Stack>
    </>
  );

  if (!opened) return null;

  const overlayZ = 400;
  const panelZ = 401;

  if (anchorRect) {
    const win = typeof window !== 'undefined' ? window : null;
    const spaceOnRight = win ? win.innerWidth - (anchorRect.right + GAP_PX) : PANEL_WIDTH;
    const fitsOnRight = spaceOnRight >= PANEL_WIDTH;
    const left = fitsOnRight
      ? anchorRect.right + GAP_PX
      : anchorRect.left - PANEL_WIDTH - GAP_FROM_DRAWER_PX;
    const leftClamped = win
      ? Math.max(GAP_PX, Math.min(left, win.innerWidth - PANEL_WIDTH - GAP_PX))
      : left;

    const topRaw = anchorRect.top;
    const topClamped = win
      ? Math.max(PANEL_VERTICAL_MARGIN, Math.min(topRaw, win.innerHeight - PANEL_MAX_VISIBLE_HEIGHT - PANEL_VERTICAL_MARGIN))
      : topRaw;

    return (
      <>
        <Box
          sx={{ position: 'fixed', inset: 0, zIndex: overlayZ }}
          aria-hidden
        />
        <Paper
          ref={clickOutsideRef}
          shadow="xl"
          radius="md"
          p="xs"
          sx={{
            position: 'fixed',
            left: leftClamped,
            top: topClamped,
            width: PANEL_WIDTH,
            maxHeight: `calc(100vh - ${PANEL_VERTICAL_MARGIN * 2}px)`,
            overflowY: 'auto',
            zIndex: panelZ,
          }}
        >
          <Group position="apart" mb="xs" sx={{ margin: '10px 15px' }}>
            <Text component="span" weight={650}>Edit Column</Text>
            <ActionIcon variant="subtle" size="sm" onClick={onClose} aria-label="Close">
              <IconX size={16} />
            </ActionIcon>
          </Group>
          {modalBodyContent}
        </Paper>
      </>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<span style={{ fontWeight: 650 }}>Edit Column</span>}
      size="350px"
      radius="md"
      withCloseButton
      closeOnClickOutside
      withOverlay={false}
      padding={'xs'}
      styles={{ header: { margin: '10px 15px' } }}
    >
      {modalBodyContent}
    </Modal>
  );
};

export default EditColumnModal;